import React from "react"
import gql from "graphql-tag"
import { useDispatch } from "react-redux"
import { useQuery } from "@apollo/client"
import { LessonPackage, CourseID } from "@coast/utils"
import { GenericStringObj } from "../types"
import {
  CouponsListDocument,
  GQLCouponForView_CourseCoupon_Fragment,
  GQLCouponForView_LessonCoupon_Fragment,
  GQLCouponForView_RoadTestCoupon_Fragment,
} from "./CouponsProvider.graphqlTypes"
import { resetAutoApplyCouponAction } from "../actions/cartActions"
import { useSelectorTyped } from "../store/configureStore"
import { buildAddressLine } from "../utils/misc"
import { GQLCouponType } from "../graphqlTypes"
import { useStudentMinutes } from "../hooks/useStudentMinutes"

gql`
  fragment CouponForView on Coupon {
    __typename
    ... on CourseCoupon {
      id
      code
      discount
      title
      courseId
      state
      isDailyCoupon
      type
      expiration
    }
    ... on LessonCoupon {
      id
      code
      discount
      type
      title
      expiration
      regions {
        id
      }
      displayInLandingPages
      onlyFirstPurchase
    }
    ... on RoadTestCoupon {
      id
      code
      discount
      type
      title
      expiration
      regions {
        id
      }
      displayInLandingPages
      onlyFirstPurchase
    }
  }

  query CouponsList($first: Int!) {
    coupons(pageQuery: { first: $first }) {
      edges {
        cursor
        node {
          ...CouponForView
        }
      }
    }
  }
`

interface CourseCouponsMap {
  [key: string]: GQLCouponForView_CourseCoupon_Fragment
}

interface LessonCouponsMap {
  [key: string]: GQLCouponForView_LessonCoupon_Fragment
}

interface RoadTestCouponsMap {
  [key: string]: GQLCouponForView_RoadTestCoupon_Fragment
}

interface CouponValidation {
  isValid: boolean
  message?: string
}

export interface CouponsState {
  address: string | undefined
  courseCoupons: CourseCouponsMap | undefined
  lessonCoupons: LessonCouponsMap | undefined
  roadTestCoupons: RoadTestCouponsMap | undefined
  dailyCourseCouponMap: GenericStringObj | undefined
  setAddress: (address: string) => void
  isCourseCodeValid: (
    code: string | undefined,
    courseId?: CourseID,
  ) => CouponValidation
  isLessonCodeValid: (
    code: string | undefined,
    regionId: string | undefined,
    lessonPackage: LessonPackage | undefined | null,
  ) => CouponValidation
}

const CouponsContext = React.createContext<CouponsState>(undefined!)

export function CouponsProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch()
  const searchAddress = useSelectorTyped((s) => s.location.searchAddress)
  const profileAddress = useSelectorTyped((s) =>
    buildAddressLine(s.firebase.profile.address),
  )
  const userId = useSelectorTyped((state) => state.firebase.auth.uid)
  const balance = useStudentMinutes(userId)
  const isFirstLessonPurchase = balance
    ? balance.drivingMinutesPurchased === 0 &&
      balance.observationMinutesPurchased === 0
    : undefined
  const [state, setState] = React.useState<CouponsState>({
    address: searchAddress ?? profileAddress,
    courseCoupons: undefined,
    lessonCoupons: undefined,
    roadTestCoupons: undefined,
    dailyCourseCouponMap: undefined,
    isCourseCodeValid: () => ({ isValid: false }),
    isLessonCodeValid: () => ({ isValid: false }),
    setAddress: (address: string) => setState((prev) => ({ ...prev, address })),
  })

  React.useEffect(() => {
    dispatch(resetAutoApplyCouponAction())
  }, [dispatch])

  useQuery(CouponsListDocument, {
    variables: { first: 1000 },
    onCompleted: (data) => {
      const dailyCourseCouponMap: GenericStringObj = {}
      const courseCouponsByCode: CourseCouponsMap = {}
      const lessonCouponsByCode: LessonCouponsMap = {}
      const roadTestCouponsByCode: RoadTestCouponsMap = {}

      for (const { node: coupon } of data.coupons.edges) {
        switch (coupon.__typename) {
          case "CourseCoupon":
            courseCouponsByCode[coupon.code] = coupon
            if (coupon.isDailyCoupon) {
              // TODO we can remove the "as CourseID" in future when we remove
              // courseSlug from the graphql query and make courseId required again.
              // MH August, 2024
              dailyCourseCouponMap[coupon.courseId as CourseID] = coupon.code
            }
            break
          case "LessonCoupon":
            lessonCouponsByCode[coupon.code] = coupon
            break
          case "RoadTestCoupon":
            roadTestCouponsByCode[coupon.code] = coupon
            break
          default:
            break
        }
      }

      const isCourseCodeValid = (
        code: string | undefined,
        courseId?: CourseID,
      ): CouponValidation => {
        if (!code || !courseId) {
          return { isValid: false }
        }

        const coupon = courseCouponsByCode?.[code]

        if (!coupon) {
          return { isValid: false, message: "Coupon not found" }
        }

        if (coupon.expiration && Date.now() >= coupon.expiration) {
          return { isValid: false, message: "Coupon has expired" }
        }

        if (coupon.courseId !== courseId) {
          return { isValid: false, message: "Coupon is not valid" }
        }

        return { isValid: true }
      }

      const isLessonCodeValid = (
        code: string | undefined,
        regionId: string | undefined,
        lessonPackage: LessonPackage | undefined | null,
      ): CouponValidation => {
        if (!code) {
          return { isValid: false }
        }

        const coupon =
          lessonCouponsByCode?.[code] ?? roadTestCouponsByCode?.[code]
        // we don't validate if first purchase here
        // because we validate when adding to cart and in the backend

        if (!coupon) {
          return { isValid: false, message: "Coupon not found" }
        }

        if (coupon.expiration && Date.now() >= coupon.expiration) {
          return { isValid: false, message: "Coupon has expired" }
        }

        if (
          regionId &&
          coupon.regions.length > 0 &&
          !coupon.regions.some((region) => region.id === regionId)
        ) {
          return {
            isValid: false,
            message: "Coupon is not valid for this region",
          }
        }

        if (!lessonPackage) {
          return { isValid: false, message: "Lesson package is required" }
        }

        if (
          coupon.type === GQLCouponType.Lesson &&
          lessonPackage.numLessons === 0
        ) {
          return { isValid: false, message: "Coupon is only valid for lessons" }
        }

        if (
          coupon.type === GQLCouponType.RoadTest &&
          lessonPackage.numRoadTests === 0
        ) {
          return {
            isValid: false,
            message: "Coupon is only valid for road tests",
          }
        }

        if (coupon.onlyFirstPurchase && isFirstLessonPurchase === false) {
          return {
            isValid: false,
            message: "Coupon is only valid for first purchase",
          }
        }

        return { isValid: true }
      }

      setState((prev) => ({
        ...prev,
        courseCoupons: courseCouponsByCode,
        lessonCoupons: lessonCouponsByCode,
        roadTestCoupons: roadTestCouponsByCode,
        dailyCourseCouponMap,
        isCourseCodeValid,
        isLessonCodeValid,
      }))
    },
  })

  return (
    <CouponsContext.Provider value={state}>{children}</CouponsContext.Provider>
  )
}

export const useCouponsContext = () => React.useContext(CouponsContext)
